import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Home, Wrench, Megaphone, FileSignature, KeyRound,
  AlertTriangle, RefreshCw, Clock,
} from "lucide-react";

const STAGE_CONFIG = [
  { key: "move_out",    label: "Move-out",     icon: Home,           tone: "border-red-500/30 bg-red-500/[0.04]",       textTone: "text-red-300" },
  { key: "turnover",    label: "Turnover",     icon: Wrench,         tone: "border-amber-500/30 bg-amber-500/[0.04]",   textTone: "text-amber-300" },
  { key: "marketing",   label: "Marketing",    icon: Megaphone,      tone: "border-orange-500/30 bg-orange-500/[0.04]", textTone: "text-orange-300" },
  { key: "application", label: "Application",  icon: FileSignature,  tone: "border-sky-500/30 bg-sky-500/[0.04]",       textTone: "text-sky-300" },
  { key: "leased",      label: "Leased",       icon: KeyRound,       tone: "border-emerald-500/30 bg-emerald-500/[0.04]", textTone: "text-emerald-300" },
];

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function KPI({ label, value, testid }) {
  return (
    <Card className="p-4 border-white/10 bg-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold text-white mt-1 font-display">{value}</div>
    </Card>
  );
}

function UnitCard({ row }) {
  const stage = STAGE_CONFIG.find((s) => s.key === row.stage) || STAGE_CONFIG[0];
  const days = row.days_in_stage;
  return (
    <Card className={`p-3 mb-2 ${stage.tone}`} data-testid={`vacancy-card-${row.property_id}-${row.unit}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-zinc-100 truncate">
            Unit {row.unit || "—"}
          </div>
          <div className="text-[11px] text-zinc-500 truncate">{row.property_name}</div>
        </div>
        {row.sla_breach && (
          <Badge
            variant="outline"
            className="border-red-500/50 text-red-300 bg-red-500/10"
            data-testid="vacancy-sla-breach"
          >
            <AlertTriangle className="h-3 w-3 mr-0.5" /> SLA
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-zinc-400 mt-1">
        <Clock className="h-3 w-3" />
        {days == null ? "—" : `${days} day${days === 1 ? "" : "s"} in stage`}
      </div>
      {row.monthly_rent != null && (
        <div className="text-[11px] text-zinc-400 mt-0.5">
          Rent <span className="font-mono text-zinc-200">{fmtMoney(row.monthly_rent)}</span>/mo
        </div>
      )}
      {row.outgoing_resident && (
        <div className="text-[11px] text-zinc-500 mt-0.5">Out: {row.outgoing_resident}</div>
      )}
      {row.incoming_resident && (
        <div className="text-[11px] text-zinc-500 mt-0.5">Applicant: {row.incoming_resident}</div>
      )}
      {row.resident_name && row.stage === "leased" && (
        <div className="text-[11px] text-zinc-500 mt-0.5">Tenant: {row.resident_name}</div>
      )}
      {row.next_action && (
        <div className={`text-[11px] mt-2 pt-2 border-t border-white/5 ${stage.textTone}`}>
          → {row.next_action}
        </div>
      )}
    </Card>
  );
}

export default function VacancyTrackerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vacancy/pipeline");
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load vacancy pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="text-sm text-zinc-400" data-testid="vacancy-loading">Loading vacancy pipeline…</div>;
  }
  const k = data?.kpis || {};
  const by = data?.by_stage || {};

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Leasing"
        title="Vacancy Tracker"
        subtitle="Every unit from move-out through cleaning, marketing, and back into a signed lease."
        right={
          <Button
            variant="outline" className="border-white/10"
            onClick={load} data-testid="vacancy-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total units tracked" value={k.total || 0} testid="vacancy-kpi-total" />
        <KPI label="Vacant" value={k.vacant || 0} testid="vacancy-kpi-vacant" />
        <KPI label="Leased" value={k.leased || 0} testid="vacancy-kpi-leased" />
        <KPI label="Avg days to lease" value={k.avg_days_to_lease == null ? "—" : `${k.avg_days_to_lease}d`} testid="vacancy-kpi-avg" />
        <KPI label="SLA breaches (30+d)" value={k.sla_breaches || 0} testid="vacancy-kpi-sla" />
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {STAGE_CONFIG.map((s) => {
          const rows = by[s.key] || [];
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-3 min-h-[300px]"
              data-testid={`vacancy-column-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`h-6 w-6 rounded-md flex items-center justify-center ${s.tone}`}>
                    <Icon className={`h-3.5 w-3.5 ${s.textTone}`} />
                  </div>
                  <div className="text-sm font-semibold text-white">{s.label}</div>
                </div>
                <Badge variant="outline" className="border-white/10 text-zinc-300">
                  {rows.length}
                </Badge>
              </div>
              {rows.length === 0 ? (
                <div className="text-xs text-zinc-500 text-center py-6" data-testid={`vacancy-empty-${s.key}`}>
                  No units here.
                </div>
              ) : (
                rows.map((r) => (
                  <UnitCard key={`${r.property_id}-${r.unit}-${r.linked_id}`} row={r} />
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
